// ============================================================
// @munawb/supabase — Incident Queries
// ============================================================

import { supabase } from '../client';
import type {
  IncidentRow, IncidentWithRelations,
  IncidentInsert, IncidentLogInsert
} from '@munawb/types';

// ==============================
// جلب الأحداث حسب فلتر
// ==============================

export interface FetchIncidentsOptions {
  reporter_id?:    string;
  dept_id?:        string;
  status?:         string;
  risk_level?:     string;
  start_date?:     string;
  end_date?:       string;
  include_closed?: boolean;
  limit?:          number;
  offset?:         number;
}

export async function fetchIncidents(opts: FetchIncidentsOptions = {}) {
  let query = supabase
    .from('incidents')
    .select(`
      *,
      department:departments(id, name),
      category:event_categories(id, main_cat, sub_cat),
      reporter:profiles!reporter_id(id, name, role)
    `)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (opts.reporter_id) query = query.eq('reporter_id', opts.reporter_id);
  if (opts.dept_id)     query = query.eq('dept_id', opts.dept_id);
  if (opts.status)      query = query.eq('status', opts.status);
  if (opts.risk_level)  query = query.eq('risk_level', opts.risk_level);
  if (opts.start_date)  query = query.gte('created_at', opts.start_date);
  if (opts.end_date)    query = query.lte('created_at', opts.end_date + 'T23:59:59');
  if (opts.limit)       query = query.limit(opts.limit);
  if (opts.offset)      query = query.range(opts.offset, opts.offset + (opts.limit ?? 50) - 1);

  const { data, error } = await query;
  if (error) throw error;
  return data as IncidentWithRelations[];
}

export async function fetchIncidentById(id: string) {
  const { data, error } = await supabase
    .from('incidents')
    .select(`
      *,
      department:departments(id, name),
      category:event_categories(id, main_cat, sub_cat),
      reporter:profiles!reporter_id(id, name, role),
      logs:incident_logs(*, actor:profiles!actor_id(id, name, role)),
      rca:quality_rca(*)
    `)
    .eq('id', id)
    .is('deleted_at', null)
    .single();

  if (error) throw error;
  return data as IncidentWithRelations;
}

export async function fetchIncidentLogs(incident_id: string) {
  const { data, error } = await supabase
    .from('incident_logs')
    .select('*, actor:profiles!actor_id(id, name, role)')
    .eq('incident_id', incident_id)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data;
}

// ==============================
// الأحداث بمستوى خطورة محدد
// ==============================
export async function fetchIncidentsByRiskLevel(risk_level: string) {
  const { data, error } = await supabase
    .from('incidents')
    .select('*, department:departments(name)')
    .eq('risk_level', risk_level)
    .not('status', 'in', '("مغلق","RCA Completed")')
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

// ==============================
// Executive Dashboard KPIs (RPC)
// ==============================
export async function fetchExecutiveDashboard(
  start_date?: string,
  end_date?: string
) {
  const { data, error } = await supabase.rpc('get_executive_dashboard', {
    p_start_date: start_date ?? null,
    p_end_date:   end_date   ?? null,
  });
  if (error) throw error;
  return data;
}
